// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import {
  ChampsPageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { ChampsPageFooterComponent } from '../shared/footer/footer.component';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'coupons'
    | 'packages'
    | 'fleets'
    | 'express-details';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-champs-coupons-preview',
  standalone: true,
  imports: [CommonModule, ChampsPageHeaderComponent, ChampsPageFooterComponent],
  templateUrl: './coupons-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./coupons-preview.component.scss'],
})
export class ChampsCouponsPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  // Page data from JSON
  headerData: any = null;
  heroSectionData: any = null;
  couponsSectionData: any = null;
  footerData: any = null;

  // Loading and animation state
  isPageLoaded = false;
  private intersectionObserver?: IntersectionObserver;
  private animatedElements = new Set<Element>();

  constructor(
    private logger: LoggerService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Initializes page content, loading state, and schedules scroll animations after a simulated load.
   * @example
   * gOnInit()
   * void
   * @param {void} none - No arguments.
   * @returns {void} Initializes component state and schedules animations; does not return a value.
   **/
  ngOnInit(): void {
    this.loadPageContent();

    // Initialize page loading state
    this.isPageLoaded = false;

    // Simulate page load animation
    setTimeout(() => {
      this.isPageLoaded = true;
      this.cdr.detectChanges();
      // Initialize scroll animations after page loads
      setTimeout(() => this.initScrollAnimations(), 100);
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.animatedElements.clear();
  }

  /**
   * Initialize scroll animations using Intersection Observer
   */
  private initScrollAnimations(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return; // Fallback for browsers without IntersectionObserver
    }

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      // Create intersection observer for scroll animations with optimized settings
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
                entry.target.classList.add('animate-in');
                this.animatedElements.add(entry.target);
                // Unobserve after animation to improve performance
                this.intersectionObserver?.unobserve(entry.target);
              }
            });
          });
        },
        {
          threshold: [0.1, 0.2], // Multiple thresholds for smoother detection
          rootMargin: '0px 0px -80px 0px', // Reduced margin for better scroll performance
        }
      );

      // Observe all animatable elements with delay to avoid blocking initial render
      setTimeout(() => {
        const elements = document.querySelectorAll('.coupons-preview .website-element');
        elements.forEach(el => {
          // Check if element is already in viewport
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;

          el.classList.add('scroll-animate');

          // If element is already visible, animate it immediately
          if (isInViewport) {
            requestAnimationFrame(() => {
              el.classList.add('animate-in');
              this.animatedElements.add(el);
            });
          } else {
            // Only observe if not already in viewport
            this.intersectionObserver?.observe(el);
          }
        });
      }, 300);
    });
  }

  /**
   * Lifecycle hook called when any data-bound input properties change; reloads page content on pageData changes and updates header navigation when pages or currentPageType change.
   * @example
   * gOnChanges({ pageData: { firstChange: false, currentValue: {/* ... */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData']) {
      if (!changes['pageData'].firstChange) {
        this.logger.debug('🎟️ Coupons preview: pageData changed, reloading content');
        this.loadPageContent();
      } else {
        // First change - still load content
        this.loadPageContent();
      }
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
  }

  /**
   * Load and normalize page data for the Coupons preview component: extracts header, footer, hero and coupons sections from either old or new API structures and updates the component state and header navigation.
   * @example
   * loadPageContent()
   * undefined
   * @returns {void} No return value; updates component properties (headerData, footerData, heroSectionData, couponsSectionData) and calls updateHeaderNavigation().
   */
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('🎟️ Loading Coupons page content:', this.pageData);

    // Extract sections from page data - support both old and new structure
    const page = this.pageData?.page || this.pageData;

    // Get header and footer from website data
    const websiteData = this.pageData?.json_data?.website || {};
    this.headerData = websiteData.header || page.header || {};
    this.footerData = websiteData.footer || page.footer || {};

    // New API structure: page.sections can be an array or an object
    if (page.sections) {
      if (Array.isArray(page.sections)) {
        // Find hero section from array
        this.heroSectionData =
          page.sections.find(
            (s: any) => s.section_id === 'hero' || s.section_type === 'hero_section'
          ) || null;

        // Find coupons section from array
        this.couponsSectionData =
          page.sections.find(
            (s: any) => s.section_id === 'coupons' || s.section_type === 'coupons_grid'
          ) || null;
      } else if (page.sections.hero_section) {
        // New API structure: page.sections is an object with hero_section property
        this.heroSectionData = page.sections.hero_section;

        // Find coupons section from sections object
        this.couponsSectionData =
          page.sections.coupons_section || page.sections.coupons_grid || null;
      } else {
        // Fallback: try to find hero section in sections object
        this.heroSectionData = page.sections.hero || page.sections['hero-section'] || null;
        this.couponsSectionData = page.sections.coupons || page.sections['coupons-section'] || null;
      }
    } else {
      // Fallback to old structure
      this.heroSectionData = page['page-title'] || {};
      this.couponsSectionData = page.coupon_card || {};
    }

    this.logger.debug('🎟️ Coupons page sections loaded:', {
      header: this.headerData,
      hero: this.heroSectionData,
      heroHeading: this.heroSectionData?.content?.heading?.text,
      heroSubheading: this.heroSectionData?.content?.subheading?.text,
      coupons: this.couponsSectionData,
      footer: this.footerData,
      couponsArray: this.couponsSectionData?.coupons,
      couponsCount: this.couponsSectionData?.coupons?.length || 0,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update headerData.navigation from this.pages and ensure a default CTA button exists.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @returns {{void}} Updates this.headerData in-place; no value is returned.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug('Updated header navigation for Coupons:', this.headerData.navigation);
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    // Check section visibility map first
    if (this.sectionVisibility && Object.keys(this.sectionVisibility).length > 0) {
      if (this.sectionVisibility[sectionKey] === false) {
        return false;
      }
    }

    // Check section active property from API
    const page = this.pageData?.page || this.pageData;
    if (page.sections && Array.isArray(page.sections)) {
      const section = page.sections.find(
        (s: any) =>
          s.section_id === sectionKey ||
          (sectionKey === 'hero' && s.section_type === 'hero_section') ||
          (sectionKey === 'coupons' && s.section_type === 'coupons_grid')
      );
      if (section && section.active === false) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get footer quick links from active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Retrieve and normalize footer social links into an array of objects containing platform, url, and icon.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'facebook', url: 'https://facebook.com/example', icon: 'facebook' }]
   * @returns {Array<{ platform: string; url: string; icon: string }>} Normalized array of social link objects.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    // Handle different data structures:
    // 1. socialLinks as array (from website builder)
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 2. socialLinks as object with platforms array (from website preview)
    if (
      footerContent?.socialLinks?.platforms &&
      Array.isArray(footerContent.socialLinks.platforms)
    ) {
      return footerContent.socialLinks.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 3. Check for social_links.platforms (alternative structure)
    if (
      footerContent?.social_links?.platforms &&
      Array.isArray(footerContent.social_links.platforms)
    ) {
      return footerContent.social_links.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 4. Check for social_links as array
    if (footerContent?.social_links && Array.isArray(footerContent.social_links)) {
      return footerContent.social_links.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.socialLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.quickLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const copyright = footerContent?.copyright;
    if (typeof copyright === 'string') {
      return copyright;
    } else if (copyright && typeof copyright === 'object' && copyright.text) {
      return copyright.text;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo (text and optional image) from the footer content, or fall back to the header logo or defaults.
   * @example
   * etFooterLogo()
   * { text: 'way', image: '' }
   * @returns {{ text?: string; image?: string }} | undefined Return an object containing optional text and image for the logo, or undefined.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Get the header logo object, resolving first from the live header element, then from page branding, and falling back to sensible defaults.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Example', link: '/', alt: 'Example logo' }
   * @param {{void}} {{none}} - No arguments.
   * @returns {{Logo}} Logo object with image, text, link and alt properties.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    // Priority 1: Check header element content (most up-to-date)
    if (headerContent?.logo) {
      // If logo has image/url, return it
      if (headerContent.logo.image || headerContent.logo.url) {
        return {
          image: headerContent.logo.image || headerContent.logo.url || '',
          text: headerContent.logo.text || 'Logo',
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
      // If logo has text but no image, return text logo
      if (headerContent.logo.text) {
        return {
          image: '',
          text: headerContent.logo.text,
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
    }
    // Priority 2: Check branding data from pageData
    if (this.pageData?.json_data?.website?.branding?.logo) {
      const brandingLogo = this.pageData.json_data.website.branding.logo;
      if (brandingLogo.url || brandingLogo.image) {
        return {
          image: brandingLogo.url || brandingLogo.image || '',
          text: brandingLogo.text || 'Logo',
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
      if (brandingLogo.text) {
        return {
          image: '',
          text: brandingLogo.text,
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
    }
    return { text: 'Logo', link: '/', image: '', alt: 'Logo' };
  }

  /**
   * Generate header navigation items from header content or fallback pages, ensuring each item has a normalized pageType and link.
   * @example
   * etHeaderNavigation()
   * [
   *   { label: "Home", link: "#home", pageType: "home", active: false },
   *   { label: "Coupons", link: "#coupons", pageType: "coupons", active: false }
   * ]
   * @returns {{NavigationItem[]}} - Array of navigation items with label, link, pageType, and optional active flag.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Return the header CTA button configuration from the current header element, falling back to sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @param {void} none - No parameters.
   * @returns {CTAButton|null} CTA button object containing text, link, backgroundColor, and textColor, or null if unavailable.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    // Header is inside hero section (like home page), so use white background
    return '#FFFFFF';
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    // New API structure: heroSectionData.content.heading.text
    if (this.heroSectionData?.content?.heading?.text) {
      return this.heroSectionData.content.heading.text;
    }
    // Fallback to old structure
    if (this.heroSectionData?.title) {
      if (typeof this.heroSectionData.title === 'string') {
        return this.heroSectionData.title;
      } else if (this.heroSectionData.title?.label) {
        return this.heroSectionData.title.label;
      }
    }
    // Check page-level title
    const page = this.pageData?.page || this.pageData;
    if (page?.title) {
      if (typeof page.title === 'string') {
        return page.title;
      } else if (page.title?.label) {
        return page.title.label;
      }
    }
    // Check section content (old structure)
    if (page?.section?.content?.heading?.text) {
      return page.section.content.heading.text;
    }
    return 'Exclusive Car Wash Coupons for Added Savings!';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    // New API structure: heroSectionData.content.subheading.text
    if (this.heroSectionData?.content?.subheading?.text) {
      return this.heroSectionData.content.subheading.text;
    }
    // Fallback to old structure
    if (this.heroSectionData?.description) {
      return this.heroSectionData.description;
    } else if (this.heroSectionData?.subtitle) {
      return this.heroSectionData.subtitle;
    }
    // Check page-level description
    const page = this.pageData?.page || this.pageData;
    if (page?.description) {
      return page.description;
    }
    // Check section content (old structure)
    if (page?.section?.content?.paragraph?.text) {
      return page.section.content.paragraph.text;
    }
    return 'Browse our latest car wash coupons and enjoy premium cleaning services at amazing prices. Better clean at a better price!';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // New API structure: heroSectionData.content.heading.style
    if (this.heroSectionData?.content?.heading?.style) {
      const headingStyle = this.heroSectionData.content.heading.style;
      const styles: any = {};
      if (headingStyle.fontWeight) styles['font-weight'] = headingStyle.fontWeight;
      if (headingStyle.fontStyle) styles['font-style'] = headingStyle.fontStyle;
      if (headingStyle.textDecoration) styles['text-decoration'] = headingStyle.textDecoration;
      if (headingStyle.textAlign || headingStyle.alignment)
        styles['text-align'] = headingStyle.textAlign || headingStyle.alignment;
      if (headingStyle.color) styles['color'] = headingStyle.color;
      if (headingStyle.font_size) styles['font-size'] = headingStyle.font_size;
      return styles;
    }
    // Fallback to old structure
    const page = this.pageData?.page || this.pageData;
    if (page?.section?.content?.heading?.style) {
      const headingStyle = page.section.content.heading.style;
      const styles: any = {};
      if (headingStyle.fontWeight) styles['font-weight'] = headingStyle.fontWeight;
      if (headingStyle.fontStyle) styles['font-style'] = headingStyle.fontStyle;
      if (headingStyle.textDecoration) styles['text-decoration'] = headingStyle.textDecoration;
      if (headingStyle.textAlign || headingStyle.alignment)
        styles['text-align'] = headingStyle.textAlign || headingStyle.alignment;
      return styles;
    }
    return {};
  }

  /**
   * Get coupon section element for selection
   */
  getCouponSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'coupons' ||
        el.id === 'coupons' ||
        (el as any).originalKey === 'hero' ||
        (el as any).originalKey === 'coupons'
    );
  }

  /**
   * Handle hero section click
   */
  onPageHeaderSectionClick(event: Event): void {
    event.stopPropagation();
    const couponElement: WebsiteElement = {
      id: 'coupons',
      type: 'coupons',
      content: {
        ...(this.heroSectionData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
      },
      selected: true,
      originalKey: 'hero',
    } as any;
    this.logger.debug('🎟️ Hero section clicked, emitting element:', couponElement);
    this.elementSelect.emit(couponElement);
  }

  /**
   * Handle coupon card section click
   */
  onCouponCardClick(event: Event): void {
    event.stopPropagation();
    const couponElement: WebsiteElement = {
      id: 'coupons',
      type: 'coupons',
      content: {
        ...(this.couponsSectionData || {}),
        coupons: this.getCoupons(),
      },
      selected: true,
      originalKey: 'coupons',
    } as any;
    this.logger.debug('🎟️ Coupon card clicked, emitting element:', couponElement);
    this.elementSelect.emit(couponElement);
  }

  /**
   * Get coupons from API data - returns array of coupon objects with images
   */
  getCoupons(): any[] {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;

    // Check for coupon image from page.section.coupon.image (from panel)
    const couponSection = page?.section?.coupon || {};
    if (couponSection.image?.src) {
      // Return single coupon with image from panel
      return [
        {
          image: couponSection.image.src,
          alt: couponSection.image.alt || 'Coupon',
          id: 'coupon-1',
        },
      ];
    }

    // Check couponsSectionData first
    if (
      this.couponsSectionData?.coupons &&
      Array.isArray(this.couponsSectionData.coupons) &&
      this.couponsSectionData.coupons.length > 0
    ) {
      return this.couponsSectionData.coupons.map((coupon: any) => ({
        ...coupon,
        image: coupon.image || coupon.image_url || coupon.src || null,
      }));
    }

    // Check if sections is an array
    if (page?.sections && Array.isArray(page.sections)) {
      const couponsSection = page.sections.find(
        (s: any) => s.section_id === 'coupons' || s.section_type === 'coupons_grid'
      );
      if (
        couponsSection?.coupons &&
        Array.isArray(couponsSection.coupons) &&
        couponsSection.coupons.length > 0
      ) {
        return couponsSection.coupons.map((coupon: any) => ({
          ...coupon,
          image: coupon.image || coupon.image_url || coupon.src || null,
        }));
      }
    }

    // Check old structure
    if (page?.coupons && Array.isArray(page.coupons) && page.coupons.length > 0) {
      return page.coupons.map((coupon: any) => ({
        ...coupon,
        image: coupon.image || coupon.image_url || coupon.src || null,
      }));
    }

    // Return default placeholder coupon if no coupons found (API under processing)
    return [
      {
        image: null, // Will use placeholder in getCouponImageUrl
        alt: 'Coupon',
        id: 'coupon-placeholder',
      },
    ];
  }

  /**
   * Get coupon image URL - from API or placeholder
   */
  getCouponImageUrl(coupon: any): string {
    // Check for image in coupon data (from API)
    if (coupon?.image) {
      return coupon.image;
    }
    if (coupon?.image_url) {
      return coupon.image_url;
    }
    if (coupon?.src) {
      return coupon.src;
    }

    // Check page.section.coupon.image (from panel)
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const couponSection = page?.section?.coupon || {};
    if (couponSection.image?.src) {
      return couponSection.image.src;
    }
    if (couponSection.coupon?.image?.src) {
      return couponSection.coupon.image.src;
    }
    if (couponSection.promoCode?.image?.src) {
      return couponSection.promoCode.image.src;
    }

    // Use placeholder image as default
    return 'assets/images/website-builder/coupons_placeholder.png';
  }

  /**
   * Handle coupon image load error
   */
  onCouponImageError(event: any): void {
    const img = event.target as HTMLImageElement;
    // Fallback to placeholder if image fails to load
    if (!img.src.includes('coupons_placeholder.png')) {
      img.src = 'assets/images/website-builder/coupons_placeholder.png';
    }
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  /**
   * Handles a footer click: stops event propagation, ensures the footer element has type/id 'footer', and forwards it to onElementClick.
   * @example
   * nFooterClick(new Event('click'))
   * undefined
   * @param {{Event}} {{event}} - The click Event object from the footer click handler.
   * @returns {{void}} No return value.
   **/
  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      // Ensure footer element has correct type and id
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get hero section background color
   */
  getHeroBackgroundColor(): string {
    // Always use the default light blue background - don't let instructions affect it
    // Check hero section layout first
    if (this.heroSectionData?.layout?.background) {
      const bg = this.heroSectionData.layout.background;
      // Only use it if it's a valid color/gradient, not affected by instructions
      if (bg && bg !== 'transparent' && !bg.includes('instruction')) {
        return bg;
      }
    }
    // Default light blue background from Figma
    return '#A8D5E2';
  }

  /**
   * Get instruction heading text
   */
  getInstructionHeading(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;

    // Check page.instructions.heading.text (API structure)
    if (page?.instructions?.heading?.text) {
      return page.instructions.heading.text;
    }

    // Check page.section.instructions.heading.text (old structure)
    if (page?.section?.instructions?.heading?.text) {
      return page.section.instructions.heading.text;
    }

    return '';
  }

  /**
   * Get instruction paragraph text
   */
  getInstructionParagraph(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;

    // Check page.instructions.paragraph.text (API structure)
    if (page?.instructions?.paragraph?.text) {
      return page.instructions.paragraph.text;
    }

    // Check page.section.instructions.paragraph.text (old structure)
    if (page?.section?.instructions?.paragraph?.text) {
      return page.section.instructions.paragraph.text;
    }

    return '';
  }

  /**
   * Get instructions element for selection
   */
  getInstructionsElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.id === 'instructions' ||
        (el as any).originalKey === 'instructions_section' ||
        (el as any).type === 'instructions'
    );
  }

  /**
   * Handle instructions section click
   */
  onInstructionsSectionClick(event: Event): void {
    event.stopPropagation();
    const instructionsElement = this.getInstructionsElement();
    if (instructionsElement) {
      this.onElementClick(instructionsElement);
    } else {
      // Create instructions element if it doesn't exist
      const newElement: WebsiteElement = {
        id: 'instructions',
        type: 'instructions',
        content: {
          instructionHeading: this.getInstructionHeading(),
          instructionParagraph: this.getInstructionParagraph(),
        },
        selected: true,
        originalKey: 'instructions_section',
      } as any;
      this.onElementClick(newElement);
    }
  }

  /**
   * Get hero section background style (for gradient support)
   */
  getHeroBackgroundStyle(): any {
    const bg = this.getHeroBackgroundColor();
    if (bg.includes('gradient') || bg.includes('linear-gradient')) {
      return { background: bg };
    }
    return { 'background-color': bg };
  }

  /**
   * Get bar height for barcode visualization
   */
  getBarHeight(char: string): number {
    // Convert character to number for consistent height
    const num = parseInt(char, 10) || 0;
    // Return height between 60% and 100% based on digit
    return 60 + num * 4;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
